import {
  BarChartOutlined,
  CarOutlined,
  CloseCircleOutlined,
  CloudDownloadOutlined,
  CloudUploadOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  EditOutlined,
  GiftOutlined,
  GlobalOutlined,
  InboxOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  TruckOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Badge } from 'antd';

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import AppShell from './AppShell';
import { UserContext } from '../context/UserProvider';
import { ParcelStatusContext } from '../context/ParcelStatusProvider';
import { parseSearchParam } from '../store/orderUtils';
import queryString from 'query-string';
import { notifyError } from '../store/utils';
import { API } from '../api';

const AdminLayout = () => {
  const { user, getBranch, isBranchRole, isLogisticRole, isCustomsRole, onChange } =
    useContext(UserContext);
  const { statusList } = useContext(ParcelStatusContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [counter, setCounter] = useState([]);

  const onSearch = useCallback((search: string) => {
    const params = parseSearchParam({ q: search });
    navigate(`/order/search?${queryString.stringify(params)}`);
  }, []);

  const fetchStatusCounter = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(`/settings/shipment_status/count`);
      setCounter(result);
    } catch (error: any) {}
  }, []);

  const logout = useCallback(async () => {
    try {
      await API.get({ apiVersion: 'api' })(`/auth/logout`);
      onChange(null);
      navigate('/');
    } catch (error: any) {
      notifyError(error.message);
    }
  }, []);

  const keys = useMemo(() => {
    const key = location.pathname
      .split('/')
      .filter((it: string) => it)
      .join('/');
    return [key];
  }, [location]);

  const openKeys = useMemo(() => {
    const keys = location.pathname.split('/').filter((it: string) => it);

    return keys;
  }, [location]);

  useEffect(() => {
    if (user) {
      fetchStatusCounter();
    }
  }, [user]);

  const items = useMemo(() => {
    const deliveryMenuItems = statusList.filter((status: any) => status.menu);
    const reports = deliveryMenuItems.map((item: any) => {
      return {
        key: `report/${(item?.cd || '').toLowerCase()}`,
        label: <Link to={`/report/${(item?.cd || '').toLowerCase()}`}>{item?.name} тайлан</Link>,
      };
    });
    let items = [
      {
        key: 'dashboard',
        icon: <DashboardOutlined />,
        label: <Link to="/dashboard">Хянах самбар</Link>,
      },
      {
        key: 'foreign',
        icon: <GlobalOutlined />,
        label: 'Гадаад захиалга',
        children: [
          {
            key: 'foreign/transports',
            label: <Link to="/foreign/transports">Тээвэр</Link>,
            icon: <CarOutlined />,
          },
          {
            key: 'foreign/package',
            label: <Link to="/foreign/package">Гадаад захиалга богц</Link>,
            icon: <InboxOutlined />,
          },
          {
            key: 'foreign/customs',
            label: <Link to="/foreign/customs">Гааль</Link>,
            icon: <SafetyCertificateOutlined />,
          },
          {
            key: 'foreign/order',
            label: <Link to="/foreign/order">Гадаад илгээмж</Link>,
            icon: <GiftOutlined />,
          },
        ],
      },
      {
        key: 'order_draft',
        label: 'Урьдчилсан захиалга',
        icon: <CloudUploadOutlined />,
        children: [
          {
            key: 'order_draft/transports',
            label: <Link to="/order_draft/transports?page=1">Тээвэр</Link>,
            icon: <CarOutlined />,
          },
          {
            key: 'order_draft/packages',
            label: <Link to="/order_draft/packages?page=1">Богц</Link>,
            icon: <InboxOutlined />,
          },
          {
            key: 'order_draft/parcels',
            label: <Link to="/order_draft/parcels?page=1">Илгээмж</Link>,
            icon: <GiftOutlined />,
          },
        ],
      },
      {
        key: 'order',
        icon: <ShoppingCartOutlined />,
        label: 'Дотоод захиалга',
        children: [
          {
            key: 'order/new',
            label: <Link to="/order/new">Захиалга бүртгэл</Link>,
            icon: <EditOutlined />,
          },
          {
            key: 'order/list',
            label: <Link to="/order/list">Захиалга жагсаалт</Link>,
            icon: <UnorderedListOutlined />,
          },
          {
            key: 'order/batch',
            label: <Link to="/order/batch">Файл бүртгэл</Link>,
            icon: <CloudUploadOutlined />,
          },
          {
            key: 'order/pending',
            label: <Link to="/order/pending">Бүрэн бус захиалга</Link>,
            icon: <ClockCircleOutlined />,
          },
          {
            key: 'order/cancel',
            label: <Link to="/order/cancel">Цуцалсан захиалга</Link>,
            icon: <CloseCircleOutlined />,
          },
        ],
      },
      {
        key: 'delivery',
        label: 'Хүргэлт',
        icon: <TruckOutlined />,
        children: deliveryMenuItems.map((item: any) => {
          const count: any = counter.find((ct: any) => ct.parcelStatusNo === item.no);
          return {
            key: `delivery/${(item?.cd || '').toLowerCase()}`,
            label: (
              <Link to={`/delivery/${(item?.cd || '').toLowerCase()}`}>
                <span style={{ marginRight: 10 }}>{item?.name}</span>
                <Badge rootClassName="custom-badge" count={count?.count || 0} color="green">
                  {' '}
                </Badge>
              </Link>
            ),
          };
        }),
      },
      {
        key: 'report',
        label: 'Тайлан',
        icon: <BarChartOutlined />,
        children: [
          {
            key: 'report/new',
            label: <Link to="/report/new">Захиалгын тайлан</Link>,
          },
          ...reports,
          {
            key: 'report/shipping',
            label: <Link to="/report/shipping">Хүргэлтийн нэгдсэн тайлан</Link>,
          },
        ],
      },
      {
        key: 'settings',
        label: 'Тохиргоо',
        icon: <SettingOutlined />,
        children: [
          {
            key: 'settings/branch',
            label: <Link to="/settings/branch">Салбарууд</Link>,
          },
          {
            key: 'settings/branch/staff',
            label: <Link to="/settings/branch/staff">Салбарын ажилтан</Link>,
          },
          {
            key: 'settings/branch/delivery',
            label: <Link to="/settings/branch/delivery">Салбарын хүргэгч</Link>,
          },
          {
            key: 'settings/customer',
            label: <Link to="/settings/customer">Харилцагч</Link>,
          },
          {
            key: 'settings/customer/staff',
            label: <Link to="/settings/customer/staff">Харилцагч Ажилтан</Link>,
          },
          {
            key: 'settings/address',
            label: <Link to="/settings/address">Хаягийн жагсаалт</Link>,
          },
          {
            key: 'settings/area',
            label: <Link to="/settings/area">Бүсчлэл</Link>,
          },
          {
            key: 'settings/tariff',
            label: <Link to="/settings/tariff?page=1&size=10">Тариф</Link>,
          },
          {
            key: 'settings/sorting',
            label: <Link to="/settings/sorting">Ангилагч</Link>,
          },
          {
            key: 'settings/shipment_status',
            label: <Link to="/settings/shipment_status">Хүргэлтийн төлөв</Link>,
          },
          {
            key: 'settings/page',
            label: <Link to="/settings/page">Хуудас</Link>,
          },
          {
            key: 'settings/options',
            label: <Link to="/settings/options">Үндсэн параметр</Link>,
          },
          {
            key: 'settings/barcode',
            label: <Link to="/settings/barcode">Баркодны тохиргоо</Link>,
          },
          {
            key: 'settigs/delivery',
            label: 'Хүргэлтийн тохиргоо',
            children: [
              {
                key: '/settings/delivery/time',
                label: <Link to="/settings/delivery/time">Хүргэх хугацаа</Link>,
              },
            ],
          },
        ],
      },
      {
        key: 'app',
        icon: <CloudDownloadOutlined />,
        label: (
          <a download={true} href="/images/app-release.apk">
            Апп татах
          </a>
        ),
      },
    ];

    if (isBranchRole) {
      items = items.filter(
        (item: any) => ['dashboard', 'order', 'delivery', 'app'].indexOf(item.key) > -1
      );
    }

    if (isLogisticRole) {
      items = items.filter((item: any) => ['settings'].indexOf(item.key) < 0);
    }
    if (isCustomsRole) {
      items = items.filter(
        (item: any) => ['dashboard', 'foreign', 'order_draft'].indexOf(item.key) > -1
      );
    }
    return items;
  }, [statusList, counter]);

  // Not logged in: go straight to the login page without drawing the admin layout
  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <AppShell
      user={user}
      items={items}
      keys={keys}
      openKeys={openKeys}
      onSearch={onSearch}
      onLogout={logout}
      logoPadding="17px 17px 17px 25px"
    />
  );
};
export default AdminLayout;
